import { Link } from 'react-router-dom';
import type { LiveEventSummary } from '@nk/shared';
import { useLocale } from '@/i18n/locale-context';
import { formatDate } from '@/lib/format';
import { Icon } from '@/components/shell';
import { EmptyState } from '@/components/ui';

/**
 * The live-updates card from the supplied design — a preview that links to the event.
 *
 * The *timeline* is Phase 11's, over SSE; this shows which coverage is running and gets
 * a reader into it. The blinking dot is the design's, and the global reduced-motion rule
 * in `styles.css` stops it for anyone who asked.
 */
export function LivePreview({ events }: { events: LiveEventSummary[] }) {
  const { locale, t } = useLocale();
  const live = events.filter((event) => event.status === 'live');
  const shown = (live.length > 0 ? live : events).slice(0, 3);

  if (shown.length === 0) {
    return <EmptyState title={t('live.none')} />;
  }

  return (
    <div className="rounded-xl border border-line bg-white p-4">
      <ul className="flex flex-col">
        {shown.map((event) => (
          <li key={event.id} className="border-b border-line last:border-b-0">
            <Link to={event.path} className="group flex flex-col gap-1.5 py-3">
              <span className="flex items-center gap-2">
                {event.status === 'live' && (
                  <span aria-hidden="true" className="h-2 w-2 animate-blink rounded-full bg-red" />
                )}
                <span className="text-2xs font-extrabold uppercase tracking-wider text-red">
                  {event.status === 'live' ? t('live.now') : formatDate(event.startTime, locale)}
                </span>
              </span>
              <span className="text-base font-bold text-ink transition group-hover:text-red">
                {event.title}
              </span>
              <span className="text-xs text-muted">
                {t('live.updateCount', { count: event.updateCount })}
              </span>
            </Link>
          </li>
        ))}
      </ul>
      <Link
        to={shown[0]?.path ?? '#'}
        className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-ink transition hover:text-red"
      >
        {t('action.viewAllUpdates')}
        <Icon name="chevron-right" size={13} />
      </Link>
    </div>
  );
}
